import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import BottomNav from "@/components/app/BottomNav";

const TYPE_LABEL = { event: "rolê", game: "jogo", adega: "adega", product: "produto", avatar: "foto de perfil", user: "usuário" };
const REMOVE_LABEL = { event: "Remover rolê", game: "Remover jogo", adega: "Remover adega", product: "Remover produto", avatar: "Remover foto" };
import AdminAdegas from "@/components/app/AdminAdegas";
import AdminStats from "@/components/app/AdminStats";
import { fullUrl } from "@/components/app/ImageUploader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Admin() {
  const { user, loading } = useAuth();
  const [reports, setReports] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/reports");
      setReports(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Carregando...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const resolve = async (id) => {
    try { await api.post(`/admin/reports/${id}/resolve`); toast.success("Resolvido."); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeTarget = async (r) => {
    const urls = { event: `/admin/events/${r.target_id}`, game: `/admin/games/${r.target_id}`, adega: `/admin/adegas/${r.target_id}`, product: `/admin/products/${r.target_id}`, avatar: `/admin/users/${r.target_id}/avatar` };
    if (!window.confirm("Remover este conteúdo?")) return;
    try { await api.delete(urls[r.target_type]); await api.post(`/admin/reports/${r.report_id}/resolve`); toast.success("Removido."); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-[100dvh] pb-20 max-w-xl mx-auto">
      <header className="px-6 pt-10 pb-4">
        <h1 className="text-4xl heading font-extrabold">Admin</h1>
        <p className="text-muted-foreground">Moderação de conteúdo e adegas.</p>
      </header>

      <Tabs defaultValue="reports" className="px-6">
        <TabsList className="grid grid-cols-3 w-full mb-4">
          <TabsTrigger value="reports" data-testid="admin-tab-reports">Denúncias</TabsTrigger>
          <TabsTrigger value="adegas" data-testid="admin-tab-adegas">Adegas</TabsTrigger>
          <TabsTrigger value="stats" data-testid="admin-tab-stats">Números</TabsTrigger>
        </TabsList>
        <TabsContent value="stats"><AdminStats /></TabsContent>
        <TabsContent value="adegas"><AdminAdegas /></TabsContent>
        <TabsContent value="reports">
      <section className="space-y-3">
        {reports.length === 0 && <p className="text-sm text-muted-foreground">Sem denúncias pendentes.</p>}
        {reports.map((r) => (
          <div key={r.report_id} className="rounded-xl bg-card border border-white/10 p-4" data-testid={`report-${r.report_id}`}>
            <div className="flex items-center gap-2 mb-2 text-xs">
              <Badge variant="outline" className="border-white/15">{TYPE_LABEL[r.target_type] || r.target_type}</Badge>
              <Badge variant={r.status === "open" ? "default" : "outline"}>{r.status === "open" ? "aberta" : "resolvida"}</Badge>
              <span className="text-muted-foreground">{new Date(r.created_at).toLocaleString("pt-BR")}</span>
            </div>
            <div className="flex items-center gap-3">
              {r.target_image && <img src={fullUrl(r.target_image)} alt="" className="h-12 w-12 rounded-full object-cover" data-testid={`report-image-${r.report_id}`} />}
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate" data-testid={`report-target-name-${r.report_id}`}>{r.target_name || "(item removido ou sem nome)"}</div>
                <div className="text-sm">{r.reason}</div>
              </div>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">id: {r.target_id}</div>
            {r.status === "open" && (
              <div className="flex gap-2 mt-3">
                {r.target_type !== "user" && (
                  <Button variant="destructive" size="sm" onClick={()=>removeTarget(r)} className="press" data-testid={`admin-remove-${r.report_id}`}>{REMOVE_LABEL[r.target_type]}</Button>
                )}
                <Button variant="outline" size="sm" onClick={()=>resolve(r.report_id)} className="press" data-testid={`admin-resolve-${r.report_id}`}>Marcar como resolvido</Button>
              </div>
            )}
          </div>
        ))}
      </section>
        </TabsContent>
      </Tabs>
      <BottomNav />
    </div>
  );
}
